import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import { dateLocalizationConfig } from '@lotta/shared/util-locale';
import { useCallback } from 'react';
import { DateTime } from 'luxon';
import { useLocale } from '../use-locale/use-locale';

/**
 * Hook which returns functions for formatting dates and numbers based on the
 * current locale and timezone (based on retail unit).
 *
 * Example: '2022-01-02' => '2/1/2022' or 1000000 => '1.000.000'
 *
 * Accepts either only date (eg '2022-01-02'), or a complete datetime string
 * (eg '2022-01-02T12:00:00Z'). For the later the configured timezone of the
 * retailunit will affect the output date.
 */
export const useFormatWithLocale = () => {
  const { language, locale, retailUnit } = useLocale();
  const { data } = useGetRetailUnitQuery({ retailUnit });

  // Fallback to ISO date format if the locale is missing in our config
  const format = dateLocalizationConfig[retailUnit]?.[language]?.format ?? 'yyyy-LL-dd';

  // Fallback to undefined while loading/error should be fine since it will
  // then use the user's local timezone which should be more or less correct.
  const timeZone = data?.retailUnit.timeZone;

  const dateWithLocale = useCallback(
    (date: string) => {
      return DateTime.fromISO(date, { zone: timeZone }).toFormat(format);
    },
    [format, timeZone]
  );

  const numberWithLocale = useCallback(
    (number: number, options: Intl.NumberFormatOptions = {}) => {
      return number.toLocaleString(locale, options);
    },
    [locale]
  );

  return { dateWithLocale, numberWithLocale };
};
